import type { TranslateService } from '@ngx-translate/core';

import type { OccupancyRestrictionsInterface } from '@valk-nx/core/lib/interfaces/hotels.interface';
import type { OccupancyLabelPipe } from '@valk-nx/core/lib/pipes/occupancy-label/occupancy-label.pipe';

import type {
	OccupancyInterface,
	OccupancySettingsInterface,
} from '../interfaces/occupancy.interface';

export class OccupancyHelper {
	// Shiji does not support children, infants or multiple rooms in group
	// reservations; this is the single source of truth for that policy.
	static readonly groupBookingLimits = {
		maxRooms: 1,
		maxChildren: 0,
		maxInfants: 0,
	} as const;

	// Clamps an occupancy selection to what a group reservation supports.
	// Returns the input array untouched when it is already compatible, so
	// callers can detect a correction with a reference check.
	static clampToGroupBooking(
		occupancy: OccupancyInterface[],
	): OccupancyInterface[] {
		const { maxRooms, maxChildren, maxInfants } =
			OccupancyHelper.groupBookingLimits;

		const isCompatible =
			occupancy.length <= maxRooms &&
			occupancy.every(
				(room) =>
					room.children <= maxChildren && room.infants <= maxInfants,
			);

		if (isCompatible) {
			return occupancy;
		}

		return occupancy.slice(0, maxRooms).map((room) => ({
			adults: room.adults,
			children: maxChildren,
			infants: maxInfants,
		}));
	}

	// Narrows a hotel's occupancy restrictions to what a group reservation
	// supports, so restriction-driven UI enforces the policy without
	// special-casing group bookings.
	static applyGroupBookingRestrictions(
		restrictions: OccupancyRestrictionsInterface,
	): OccupancyRestrictionsInterface {
		const { maxRooms, maxChildren, maxInfants } =
			OccupancyHelper.groupBookingLimits;

		return {
			...restrictions,
			children: {
				...restrictions.children,
				max: maxChildren,
				default: 0,
			},
			infants: {
				...restrictions.infants,
				max: maxInfants,
				default: 0,
			},
			maxOccupancies: maxRooms,
		};
	}

	static getOccupancyString(
		occupancy: OccupancyInterface,
		translateService: TranslateService,
		occupancyLabelPipe: OccupancyLabelPipe,
	): string {
		const entries: [number, 'adults' | 'children' | 'infants'][] = [
			[occupancy.adults, 'adults'],
			[occupancy.children, 'children'],
			[occupancy.infants, 'infants'],
		];

		return entries
			.filter(([amount, key]) => key === 'adults' || amount > 0)
			.map(([amount, key]) =>
				translateService.instant(
					occupancyLabelPipe.transform(key, { amount }),
					{ amount },
				),
			)
			.join(', ');
	}

	// Returns the stepper maximum for one occupancy type, frozen at the
	// current value once the max persons total is reached. Infants only
	// count towards (and are limited by) that total when infantsCountToTotal
	// is set.
	static maxOccupancy(
		occupancyValues: OccupancyInterface,
		restrictions:
			OccupancyRestrictionsInterface | OccupancySettingsInterface,
		occupancyType: keyof OccupancyInterface,
	): number {
		const infantsCountToTotal = restrictions.infantsCountToTotal ?? false;

		if (occupancyType === 'infants' && !infantsCountToTotal) {
			return restrictions.infants.max;
		}

		const maxPersons = restrictions.maxPersons ?? Infinity;
		const totalPersons =
			occupancyValues.adults +
			occupancyValues.children +
			(infantsCountToTotal ? occupancyValues.infants : 0);

		if (totalPersons >= maxPersons) {
			return occupancyValues[occupancyType];
		}
		return restrictions[occupancyType].max;
	}

	//Check if occupancy exceeds the max persons limit and remove excess persons
	static enforceMaxPersonsLimit(
		occupancy: OccupancyInterface,
		restrictions: OccupancyRestrictionsInterface,
	): OccupancyInterface {
		let { adults, children, infants } = occupancy;
		const {
			adults: adultsRestr,
			children: childrenRestr,
			infants: infantsRestr,
			maxPersons,
		} = restrictions;
		const infantsCountToTotal = restrictions.infantsCountToTotal ?? false;

		if (maxPersons === undefined) {
			return { adults, children, infants };
		}

		const totalPersons = () =>
			adults + children + (infantsCountToTotal ? infants : 0);

		while (totalPersons() > maxPersons) {
			if (infantsCountToTotal && infants > infantsRestr.min) {
				infants--;
			} else if (children > childrenRestr.min) {
				children--;
			} else if (adults > adultsRestr.min) {
				adults--;
			} else {
				break;
			}
		}
		return { adults, children, infants };
	}

	static enforceCurrentOccupancyLimit(
		currentOccupancy: OccupancyInterface[],
		maxOccupancies: number,
	): OccupancyInterface[] {
		if (currentOccupancy.length > maxOccupancies) {
			return currentOccupancy.slice(0, maxOccupancies);
		}
		return currentOccupancy;
	}
}
